/* eslint-disable react/no-array-index-key */
import React, {useEffect, useState, useCallback, useContext} from 'react'
import PropTypes from 'prop-types'
import {
  AddIcon,
  Alert,
  ButtonLink as ButtonLink_,
  Col,
  CustomerDialogButtonRow,
  H3,
  Row,
  WarningAlert,
  XIcon,
} from '@modifi/ui'
import {
  Form,
  FormActionsContext,
  FormDataContext,
  FormInput,
  SubmitBehavior,
} from '@modifi/plugin-forms'
import {useTranslation} from '@modifi/plugin-i18n'
import {remove, sum, times} from 'ramda'
import {InferPropTypes} from '@modifi/typescript-utils'
import {DetailsTable} from '../../../../styles/common'
import TransactionBox from '../../components/transaction-box'
import {TextAreaWithContainer} from './inputs'
import NumberFormatInput from '../../../../components/number-format-input'
import {accountConfigShape} from '../../constants'
import {useAbility} from '../../../../constants/permissions'
import {TRANSACTION} from '../../../../constants/section'

const ButtonLink = ButtonLink_ as React.ComponentType<any>

const propTypes = {
  // from parent
  params: PropTypes.shape({
    transaction: PropTypes.shape({
      amount: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired,
      valueDate: PropTypes.string.isRequired,
      purpose: PropTypes.string.isRequired,
      accountConfig: PropTypes.shape(accountConfigShape).isRequired,
      id: PropTypes.number,
      isPassThrough: PropTypes.bool.isRequired,
    }).isRequired,
    amount: PropTypes.string,
  }).isRequired,

  // mapStateToProps
  isSplitting: PropTypes.bool.isRequired,
  splitError: PropTypes.string,
  isRelevant: PropTypes.bool.isRequired,

  // mapDispatchToProps
  splitTransaction: PropTypes.func.isRequired,
  cleanupSplit: PropTypes.func.isRequired,
  fetchTransactions: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}

const defaultProps = {
  splitError: null,
}

type SplitTransactionDialogProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const SplitTransactionDialog: React.FC<SplitTransactionDialogProps> = ({
  splitTransaction,
  params: {transaction},
  fetchTransactions,
  handleClose,
  isRelevant,
  isSplitting,
  splitError,
  cleanupSplit,
}) => {
  const {t} = useTranslation('moneta')
  const [splits, setSplits] = useState(2) // minimum is 2
  const [focus, setFocus] = useState(0)
  useEffect(() => () => cleanupSplit(), [cleanupSplit])

  const {data, changeData} = useContext(FormDataContext)

  const handleAddPart = useCallback(
    (index: number) => {
      setSplits(splits + 1)
      setFocus(index + 1)
    },
    [splits]
  )

  const handleDeletePart = useCallback(() => {
    setSplits(splits - 1)
  }, [splits])

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const activeElement = document?.activeElement as HTMLInputElement

      // check if the active element is the comment input, and if so, bypass the custom paste logic
      if (activeElement?.name === 'comment') {
        return // let the default paste behavior occur for the comment input
      }

      e.preventDefault()

      const clipboardData = e?.clipboardData || (window as any)?.clipboardData
      const pastedData = clipboardData?.getData('Text')

      if (!pastedData) {
        return
      }

      // split pasted data by new lines and tabs (rows and columns)
      const rows = pastedData.split('\n').filter(Boolean) // Each row
      const newSplits = rows.map(
        (row: string) =>
          row
            .split('\t') // split by tabs (columns)
            // clean value: remove commas and extra spaces - possible fix for windows
            .map(value => value.trim().replace(/,/g, '').replace(/\s+/g, ''))
            .filter(value => value && parseFloat(value) !== 0) // filter out empty strings and zeroes
      )

      const isValid = newSplits?.every((row: any) =>
        row.every((value: string) => !Number.isNaN(parseFloat(value)))
      )

      if (isValid) {
        // calculate the total number of valid fields needed
        const totalFieldsNeeded = newSplits.reduce((acc: any, row: any) => acc + row.length, 0)
        setSplits(Math.max(totalFieldsNeeded, splits))

        let totalValidFields = 0 // track the overall part index

        newSplits.forEach((row: any) => {
          row.forEach((value: string) => {
            if (value.trim() !== '') {
              const partField = `part-${totalValidFields}`
              changeData(partField, value)
              totalValidFields += 1 // increment the overall part index
            }
          })
        })

        // set focus to the last non-empty field
        setFocus(totalFieldsNeeded - 1)
      }
    },
    [changeData, splits]
  )

  const commentMinLengthMessage = useCallback(
    ({minLength}: {minLength: number}) =>
      t('split-transaction.dialog.input-comment.min-length.warning', {minLength}),
    [t]
  )

  const amountMinLengthMessage = useCallback(
    ({minLength}: {minLength: number}) =>
      t('split-transaction.dialog.input-amount.min-length.warning', {minLength}),
    [t]
  )

  useEffect(() => {
    const pasteListener = (event: Event) => handlePaste(event as ClipboardEvent)
    window.addEventListener('paste', pasteListener)

    return () => {
      window.removeEventListener('paste', pasteListener)
    }
  }, [handlePaste])

  const ability = useAbility()
  const isAllowed = ability.can('create', TRANSACTION)

  const validateAmounts = useCallback(
    (index: number, value: string, changes: Record<string, any>) => {
      const parts = Object.keys(changes)
        .filter(key => key.startsWith('part-'))
        .map(key => changes[key])
      const existingParts = remove(index, 1, parts)
      // validation of total amount happens at the last amount field
      if (
        index > 0 &&
        index === parts.length - 1 &&
        sum([...existingParts, value]).toFixed(2) !== transaction?.amount
      ) {
        return t('split-transaction.dialog.input-amount.mismatch-amounts.message', {
          amount: transaction?.amount,
        })
      }
      return null
    },
    [t, transaction?.amount]
  )

  const handleSubmit = useCallback(
    async (evt: any) => {
      evt.preventDefault()
      try {
        await splitTransaction({
          transactionId: transaction.id,
          changes: data,
        })
      } catch (error) {
        // do nothing
        return false
      }
      fetchTransactions({isRelevant})
      return handleClose()
    },
    [data, fetchTransactions, handleClose, isRelevant, splitTransaction, transaction.id]
  )

  return (
    <>
      {!isAllowed && (
        <Row mt={30} mb={10}>
          <Col col={10} offset={1}>
            <WarningAlert>{t('warning.alert.permission-denied')}</WarningAlert>
          </Col>
        </Row>
      )}
      <Row justifyContent="center">
        <Col auto>
          <H3 my={40}>{t('split-transaction.dialog.title')}</H3>
        </Col>
      </Row>

      <DetailsTable>
        <TransactionBox {...transaction} inverseBackground />
      </DetailsTable>

      <Row>
        <Col>
          {times(
            index => (
              <Row
                justifyContent="center"
                alignItems="center"
                key={`part-${index}`}
                mt={index === 0 && !splitError ? 40 : 0}
              >
                <Col col={4} order={2}>
                  <FormDataContext.Consumer>
                    {({getChanges}) => (
                      <FormInput
                        placeholder={`${
                          index === 0 ? t('split-transaction.dialog.input-amount.placeholder') : ''
                        }`}
                        inputComponent={NumberFormatInput}
                        name={`part-${index}`}
                        decimalScale={2}
                        fixedDecimalScale
                        thousandSeparator
                        isNumericString
                        collapse
                        noBlur
                        allowNegative={false}
                        autoFocus={index === focus}
                        minValue={0.01}
                        minValueMessage={t(
                          'dialog.split_transaction.amount_input.error_message.min_value'
                        )}
                        minLength={1}
                        minLengthMessage={amountMinLengthMessage}
                        validationFunction={value => validateAmounts(index, value, getChanges())}
                      />
                    )}
                  </FormDataContext.Consumer>
                </Col>
                <Col auto order={1}>
                  {index === splits - 1 && (
                    <ButtonLink type="button" onClick={() => handleAddPart(index)}>
                      <AddIcon height={24} width={24} />
                    </ButtonLink>
                  )}
                </Col>
                <FormDataContext.Consumer>
                  {({clearData}) => (
                    <Col auto order={3}>
                      {index === splits - 1 && (
                        <ButtonLink
                          type="button"
                          onClick={() => {
                            clearData(`part-${index}`)
                            handleDeletePart()
                          }}
                          active={index !== 1}
                        >
                          <XIcon height={24} width={24} />
                        </ButtonLink>
                      )}
                    </Col>
                  )}
                </FormDataContext.Consumer>
              </Row>
            ),
            splits
          )}
        </Col>
      </Row>
      <Row mt={40}>
        <Col col={8} offset={2}>
          <FormInput
            name="comment"
            label={t('split-transaction.dialog.input-comment.label')}
            inputComponent={TextAreaWithContainer}
            rows={5}
            minLength={1}
            minLengthMessage={commentMinLengthMessage}
          />
        </Col>
      </Row>
      {splitError && (
        <Row mt={30} mb={10}>
          <Col col={8} offset={2}>
            <Alert type="danger">{splitError}</Alert>
          </Col>
        </Row>
      )}
      <Row justifyContent="center">
        <Col col={6}>
          <FormActionsContext.Consumer>
            {ctx => (
              <CustomerDialogButtonRow
                canSubmit={
                  ctx.canSubmit &&
                  ctx.hasChanges &&
                  !ctx.validationPending &&
                  !isSplitting &&
                  isAllowed
                }
                onSubmitClick={handleSubmit}
                submitText={t('split-transaction.dialog.submit.cta')}
                hasCancel
                cancelText={t('split-transaction.dialog.cancel.cta')}
                onCancelClick={handleClose}
                loading={isSplitting}
                leftSpacing={0}
                rightSpacing={0}
              />
            )}
          </FormActionsContext.Consumer>
        </Col>
      </Row>
    </>
  )
}

SplitTransactionDialog.propTypes = propTypes
SplitTransactionDialog.defaultProps = defaultProps

const SplitTransactionDialogWrapper = (props: any) => (
  <Form submitBehavior={SubmitBehavior.SUBMIT_DISABLED_UNLESS_VALID}>
    <SplitTransactionDialog {...props} />
  </Form>
)

export default SplitTransactionDialogWrapper
